import _ from 'underscore';
import queryString from 'query-string';
import localeUtil from 'locale';

import { ACCOUNT_MODE } from '@bingads-webui-campaign/campaign-ui-common';
import { getLength } from '@bingads-webui-campaign/special-characters';
import { useIsUnifiedProduct } from '@bingads-webui-campaign-react/up-context';
import { getParsedIndexAndProperty } from '@bingads-webui-react/error-mapper';
import { getTimezoneI18nString } from '@bingads-webui-campaign-react/help';

import {
  AdType,
  OdataType,
  UpCampaignGoals,
  TermsAndConditionLink,
  DAYS_OF_MONTH,
  MediaType,
  ACCOUNT_BUDGET_EXCEED_CODE,
  UpSignupWizardCouponClassForProd,
  UpSignupWizardCouponClassForNonProd,
  PI_TYPE_CREDIT_CARD,
  PI_TYPE_PAYPAL,
  PI_TYPE_OFFLINE_PAYMENT_METHOD,
  PerfType,
} from './constants';

export function getCampaignGoals(goal) {
  if (!_.isEmpty(goal)) {
    return goal.split(',').map(g => g.trim());
  }
  return [];
}

/**
 * Aggregates individual properties in an array of objects
 * and returns a single object with accumulated values.
 * Eg. [{a:1, b:1}, {a:2, b:1}] --> {a:3, b:2}
 * @param {Array} objectList list of objects to be aggregated.
 * @returns {Object} accumulated values in a single object.
 */
export const AggregateValues = (objectList) => {
  const aggregated = _.reduce(objectList, (result, current) => {
    const accumulated = _.mapObject(current, (value, key) =>
      value + (result[key] || 0));
    return accumulated;
  });

  const aggregatedItem = aggregated || {};
  return aggregatedItem;
};

export function getAdType(ad) {
  const odataType = ad && ad['@odata.type'];
  const odataAdTypeMap = {
    '#Model.SearchAd': AdType.SearchAd,
    '#Model.ImageAd': AdType.ImageAd,
    '#Model.VideoAd': AdType.VideoAd,
  };
  return odataAdTypeMap[odataType];
}

export const ComputeEngagementsMetricsForOrganicPost = (post) => {
  const activityType = post[OdataType].replace(/^#Model./g, '');

  const {
    Likes,
    Shares,
    Comments,
    Replies,
    Retweets,
  } = post;
  switch (activityType) {
    case 'FacebookPost':
      return {
        ...post,
        Engagements: (_.isNumber(Likes) ? Likes : 0) +
          (_.isNumber(Shares) ? Shares : 0) +
          (_.isNumber(Comments) ? Comments : 0),
      };
    case 'InstagramPost':
      return {
        ...post,
        Engagements: (_.isNumber(Likes) ? Likes : 0) +
          (_.isNumber(Comments) ? Comments : 0),
      };
    case 'TwitterPost':
      return {
        ...post,
        Engagements: (_.isNumber(Replies) ? Replies : 0) +
          (_.isNumber(Retweets) ? Retweets : 0),
      };
    default:
      return post;
  }
};

export function getIndexVersion() {
  const regex = /^[\d|\w]{8}$/;
  const matches = regex.exec(document.lastChild.nodeValue);

  return matches ? matches[0] : '';
}

export function getTimestamp() {
  return window.performance ? performance.now() : new Date().getTime();
}

export function getInlineErrorOfInput({
  errors,
  inputValue,
  field,
  schema,
  legacyI18n,
}) {
  if (!_.isEmpty(errors)) {
    return _.values(errors);
  }
  const filedSchema = schema.properties[field] || {};
  const stringLength = getLength(inputValue, filedSchema.distinguishWideChar);
  return filedSchema.maxLength && stringLength > filedSchema.maxLength ?
    [legacyI18n.getString('Validation_String_Max_Length', { maxlength: filedSchema.maxLength })] : [];
}

export function PluckPropertyFromResponse(response, property) {
  return _.chain(response)
    .pluck(property)
    .uniq()
    .value();
}

export function IsWebSiteInvalid(error) {
  if (error && error.responseJSON) {
    const errorCodes = PluckPropertyFromResponse(error.responseJSON, 'Code');
    const properties = PluckPropertyFromResponse(error.responseJSON, 'Property');
    return (_.contains(errorCodes, 'InvalidValue') || _.contains(errorCodes, 'InvalidWebsiteUrl'))
      && _.contains(properties, 'Website');
  }
  return false;
}

export function isExceedDailyBudgetError(error) {
  if (error && error.responseJSON) {
    const errorCodes = PluckPropertyFromResponse(error.responseJSON, 'Code');
    const properties = PluckPropertyFromResponse(error.responseJSON, 'Property');
    return _.contains(errorCodes, ACCOUNT_BUDGET_EXCEED_CODE) && _.contains(properties, 'Budget');
  }
  return false;
}

export function isFbPermissionError(error) {
  if (error && error.responseJSON) {
    const errorCodes = PluckPropertyFromResponse(error.responseJSON, 'Code');

    return _.contains(errorCodes, 'NeedAdminPermission') ||
      _.contains(errorCodes, 'NeedBusinessAdminPermission');
  }
  return false;
}

export function getUpCampaignGoalOptions(campaignGoal) {
  const isGoalValid = !_.isEmpty(campaignGoal);
  const campaignGoalOptions = {
    IncreaseWebsiteVisit: isGoalValid
      && campaignGoal.indexOf(UpCampaignGoals.IncreaseWebsiteVisit.value) > -1,
    IncreaseCallToBusiness: isGoalValid
      && campaignGoal.indexOf(UpCampaignGoals.IncreaseCallToBusiness.value) > -1,
    IncreaseBusinessLocationVisit: isGoalValid
      && campaignGoal.indexOf(UpCampaignGoals.IncreaseBusinessLocationVisit.value) > -1,
  };
  return campaignGoalOptions;
}

export function getSortedFilteredCountries(countries, currencyCountriesMappings, currency) {
  const availableCountries = _.findWhere(
    currencyCountriesMappings,
    { currency }
  );
  const filteredCountries = availableCountries ?
    _.filter(
      countries,
      item => _.contains(availableCountries.countries, item.value.Code)
    ) : countries;
  // 'co' is a optional parameter for localeCompare function
  // a general name of variant collations for certain locales
  return _.pluck(filteredCountries.sort((item1, item2) =>
    item1.value.Name.localeCompare(item2.value.Name, 'co', { sensitivity: 'base' })), 'value');
}

export function convertTimeZonesForCcForm(i18n, timeZones) {
  return _.map(timeZones, item => ({
    Value: item.id,
    Text: i18n.getString(getTimezoneI18nString(item.id)),
  }));
}

export function useApplicationName(forceUpContent = false) {
  const isUnifiedProduct = useIsUnifiedProduct();
  return (isUnifiedProduct || forceUpContent) ? 'Microsoft Advertising' : 'Digital Marketing Center';
}

export function useUpHelpLink(dmcQuery, upQuery) {
  const isUnifiedProduct = useIsUnifiedProduct();
  return isUnifiedProduct ? upQuery : dmcQuery;
}

export function getTrackingInfo() {
  const locationHref = window.location.href;
  const options = queryString.parseUrl(locationHref, { parseFragmentIdentifier: true });
  return {
    trackingId: options.query.t_id,
    sourceType: options.query.s_cid,
  };
}

export function getErrorsArray(response) {
  let errors = [];
  if (_.isArray(response)) {
    errors = response;
  } else if (_.isObject(response)) {
    if (response['@odata.context'] && _.isArray(response.value)) {
      errors = response.value;
    } else if (_.isArray(response.responseJSON)) {
      errors = response.responseJSON;
    } else if (_.isObject(response.responseJSON) && _.isArray(response.responseJSON.Errors)) {
      errors = response.responseJSON.Errors;
    }
  }
  return errors;
}

export function updateErrorWithClientIdOrErrMsgObj(payload, response, requiredProperty = null) {
  let payloadWithClientIdOrErrMsgObj = [];
  if (_.isArray(payload)) {
    payloadWithClientIdOrErrMsgObj = payload;
  } else if (_.isObject) {
    payloadWithClientIdOrErrMsgObj = [payload];
  }

  const errors = getErrorsArray(response);
  const errorWithClientIdOrErrMsgObj = _.map(errors, (error) => {
    const { index, property } = getParsedIndexAndProperty(error.Property);
    if (index && index < payloadWithClientIdOrErrMsgObj.length
      && (!requiredProperty || requiredProperty === property)) {
      const { ClientId, ErrMsgObj } = payloadWithClientIdOrErrMsgObj[index] || {};
      return {
        ...error,
        ClientId,
        ErrMsgObj,
      };
    }
    return error;
  });

  return errorWithClientIdOrErrMsgObj;
}

export const getUpTermsAndConditionLink = (locale) => {
  let currentLocale = 'en-us';
  if (_.isString(locale) && _.has(TermsAndConditionLink, locale.toLowerCase())) {
    currentLocale = locale.toLowerCase();
  }
  return TermsAndConditionLink[currentLocale];
};

export const ifUPAccountExist = (serverContext = {}) =>
  serverContext && serverContext.CurrentAccount &&
  `${serverContext.CurrentAccount.AccountMode}` === `${ACCOUNT_MODE.UNIFIED_SMART}`;

export function isBusinessNameEditorialError(err = []) {
  const locationErr = _.find(err, info => info.McaEntityType === 'LocationAdExtension');
  if (locationErr && !_.isEmpty(locationErr.EditorialRejectReason)) {
    return !!_.find(locationErr.EditorialRejectReason, r => r.ViolatingLocation === 'LocationAdExtensionBusinessName' || r.ViolatingLocation === 'BusinessName');
  }
  return false;
}

export const getCampaignPageUrl = (serverContext, page) => {
  const {
    CurrentCustomer,
    CurrentAccount,
    CurrentUser,
    Urls,
  } = serverContext || {};
  const { Id: customerId } = CurrentCustomer || {};
  const { Id: accountId } = CurrentAccount || {};
  const { Id: userId } = CurrentUser || {};
  const { CampaignAzureDomain } = Urls || {};
  const host = CampaignAzureDomain || window.location.origin;
  const pageStr = !_.isUndefined(page) ? `/${page}` : '';
  const aidStr = !_.isUndefined(accountId) ? `&aid=${accountId}` : '';
  return `${host}/campaign/vnext${pageStr}?cid=${customerId}${aidStr}&uid=${userId}`;
};

export const getMonthlyBudget = dailyBudget => Math.floor(DAYS_OF_MONTH * dailyBudget);

export function getDailyBudget(monthlyBudget) {
  const dailyBudget = monthlyBudget / DAYS_OF_MONTH;
  return Number.parseFloat(dailyBudget.toFixed(2));
}

export const getMediaType = (type = '') => {
  if (type.startsWith('image/')) {
    return MediaType.Image;
  } else if (type.startsWith('video/')) {
    return MediaType.Video;
  }
  return MediaType.Unknown;
};

export const getExtension = (name = '') => {
  const index = _.lastIndexOf(name, '.');
  if (index > -1) {
    return name.substring(index).toLowerCase();
  }
  return null;
};

export function stringifyGoals(campaignGoalOptions) {
  const selectedCampaignGoalsArr = _.keys(_.pick(campaignGoalOptions, value => value));
  const goal = _.isEmpty(selectedCampaignGoalsArr) ? '' : selectedCampaignGoalsArr.join(', ');

  return goal;
}

// Account name should have at least three characters,
// if length is less than 3, add underscores (e.g. _a_)
export function getNameBeforeAccountActivate(name) {
  const length = (name && name.length) || 0;
  let result = name;
  if (name && length < 3) {
    result = `_${name}_`;
  }
  return result;
}

export function isBackupCoupon(classStringValue) {
  const backupCoupons = [
    UpSignupWizardCouponClassForProd,
    UpSignupWizardCouponClassForNonProd,
  ];
  return _.contains(backupCoupons, classStringValue);
}

export function getIsSpamEmail(originalEmail) {
  if (_.isEmpty(originalEmail)) {
    return true;
  }
  const email = originalEmail.toLowerCase();
  return !!(
    email.match(/dmctest/) || email.match(/uptest/) || email.match(/@mailforspam.com/) || email.match(/@mailmmo.com/)
    || email.match(/sp_test/) || email.match(/upsmartpagetest/) || email.match(/trangcode.com/) || email.match(/mailold.com/)
    || email.match(/optionvn.com/) || email.match(/microsoft.com/) || email.match(/microsoftsupport.com/)
    || email.match(/smartpagetest/) || email.match(/smartcampaignstest/)
  );
}

export const getPhoneAndCountryCode = (phoneNumber, markets, defaultCountryCode) => {
  const emptyNumber = { phoneNumber: null, prefixNumber: null, countryCode: defaultCountryCode };
  if (_.isEmpty(phoneNumber)) {
    return emptyNumber;
  }
  const phoneCountry = _.find(markets, market => phoneNumber.startsWith(market.phonePrefix));
  let phoneWithCountryPrefix = phoneNumber;
  let prefixNumber = _.get(phoneCountry, 'phonePrefix', null);

  if (_.isEmpty(phoneCountry)) {
    if (!phoneWithCountryPrefix.startsWith('+')) {
      prefixNumber = _.get(_.find(markets, market => market.countryCode === defaultCountryCode), 'phonePrefix', '');
      phoneWithCountryPrefix = prefixNumber + phoneNumber;
    } else {
      return emptyNumber;
    }
  }

  return { phoneNumber: phoneWithCountryPrefix, countryCode: _.get(phoneCountry, 'countryCode') || defaultCountryCode, prefixNumber };
};

export const getCustomerFormatPhoneNumber = (phoneNumber, prefixNumber) => {
  if (_.isEmpty(phoneNumber) || _.isEmpty(prefixNumber)) {
    return phoneNumber;
  }
  return `${prefixNumber.replace('+', '')}-${phoneNumber.replace(prefixNumber, '')}`;
};

export const getPiTypeString = (piType) => {
  let piTypeString = '';
  if (piType === PI_TYPE_CREDIT_CARD) {
    piTypeString = 'CreditCard';
  } else if (piType === PI_TYPE_PAYPAL) {
    piTypeString = 'PayPal';
  } else if (piType === PI_TYPE_OFFLINE_PAYMENT_METHOD) {
    piTypeString = 'Check';
  }
  return piTypeString;
};

const { localStorage } = window;

export const deleteItemFromLocalStorage = key => localStorage.removeItem(key);

export const getItemFromLocalStorage = (key) => {
  try {
    const val = localStorage.getItem(key);
    deleteItemFromLocalStorage(key);
    return JSON.parse(val) || {};
  } catch (e) {
    return {};
  }
};

export const setItemInLocalStorage = (key, val) => {
  const stringVal = JSON.stringify(val);
  localStorage.setItem(key, stringVal);
};

export const getEstimationRangeFromSubscriptionPlan = (subscriptionPlan) => {
  const range = _.get(subscriptionPlan, ['PerfCommitments']);
  const clicksRange = _.find(range, val => val.PerfType === PerfType.Clicks);
  const conversionRange = _.find(range, val => val.PerfType === PerfType.Conversions);
  const estimatedRange = {
    minEstimatedClick: '',
    maxEstimatedClick: '',
    minEstimatedConversion: '',
    maxEstimatedConversion: '',
  };
  if (!_.isEmpty(clicksRange)) {
    estimatedRange.minEstimatedClick = clicksRange.MinimumReturn;
    estimatedRange.maxEstimatedClick = clicksRange.MaximumReturn;
  }
  if (!_.isEmpty(conversionRange)) {
    estimatedRange.minEstimatedConversion = conversionRange.MinimumReturn;
    estimatedRange.maxEstimatedConversion = conversionRange.MaximumReturn;
  }
  return estimatedRange;
};

export const getCategoryName = (category, locale = 'en') => {
  if (category && category.Localizations) {
    const locales = _.pluck(category.Localizations, 'Locale');

    const supportedLocales = new localeUtil.Locales(locales, 'en');
    const bestLocale = (new localeUtil.Locales(locale)).best(supportedLocales).toString();

    const localization = _.find(category.Localizations, c => c.Locale === bestLocale);

    if (localization) {
      return localization.CategoryLocalization;
    }
  }

  return (category && category.CategoryName) || '';
};

export function getCallExtensionCountryCode(campaign) {
  return _.isEmpty(_.get(campaign, ['McaBusinessListing', 'PhoneNumber'])) ? undefined : _.get(campaign, ['McaBusinessListing', 'PhoneNumberCountryCode']);
}
